import { Component, type ReactNode } from "react";
import { ApiError } from "@/shared/api";
import { reportError } from "@/shared/observability";
import { Button } from "./button";

interface Props {
  /** Reported with the error ("preview", "upload-panel"). */
  where: string;
  /** What to show instead; default is a compact message with a retry. */
  fallback?: (reset: () => void, requestId?: string) => ReactNode;
  children: ReactNode;
}

/** Contains a failure to one panel so the rest of the app keeps working. */
export class ErrorBoundary extends Component<Props, { error: unknown }> {
  state = { error: null as unknown };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  componentDidCatch(error: unknown) {
    reportError(error, { where: this.props.where });
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (error === null) return this.props.children;
    const requestId = error instanceof ApiError ? error.requestId : undefined;
    if (this.props.fallback) return this.props.fallback(this.reset, requestId);
    return (
      <div role="alert" className="m-2 rounded-md border border-border bg-surface p-3 text-sm">
        <p className="font-medium">Something went wrong here.</p>
        {requestId ? <p className="text-muted">Request id: {requestId}</p> : null}
        <Button size="sm" variant="secondary" className="mt-2" onClick={this.reset}>
          Try again
        </Button>
      </div>
    );
  }
}
